import {match} from 'ts-pattern'
import {
  type QuestionForecastType,
  type ResolutionCfg,
  type ResolutionsStandalone,
  zQuestionForecastType
} from './question'
import {isString, sortBy, unique} from 'remeda'
import {compactObj, concat, filterObj, keys} from '../collection'
import {
  zResolutionValueTypes,
  ResolutionValueAny,
  SubqCfg,
  ResolutionValueOverride,
  ResolutionKeyDependents,
  ResolutionValueStandard,
  Resolutions
} from './subq'
import {queryStringSerializer} from '../queryStringSerializer'
import {z} from 'zod'
import {zx} from '../type'

export namespace Resolution {
  export const isOverrideProp = (p: keyof ResolutionCfg) => p in zResolutionValueTypes.override.shape

  const compactResolution = (r: ResolutionValueAny) =>
    filterObj(r, (k, v: any) => v != null && (!isString(v) || v != ''))

  export type ParentQuestionForResolutions = {
    forecastType: QuestionForecastType
    consequenceResolutions?: ResolutionsStandalone
  }

  /** returns the resolutions derived from the question & subquestion */
  export const resolutions = (parentQ: ParentQuestionForResolutions, subquestion?: SubqCfg) => {
    const forecastType =
      subquestion != null ?
        match(subquestion.subquestionType)
          .with('cause', 'related', () => subquestion.forecastType)
          .with('consequence', () => parentQ.forecastType)
          .exhaustive()
      : parentQ.forecastType
    const resolutionsParent =
      subquestion == null || subquestion.subquestionType == 'consequence' ?
        filterObj(parentQ.consequenceResolutions?.[forecastType] ?? {}, (k, r) => !r.deleted)
      : {}
    const resolutionsThis = filterObj(subquestion?.resolutions[forecastType] ?? {}, (k, r) =>
      isStandard(r) ? !r.deleted
      : isOverride(r) ? resolutionsParent[k] != null
      : true
    )
    const combined = sortBy(
      unique(concat(keys(resolutionsParent), resolutionsThis ? keys(resolutionsThis) : undefined)).map(k => {
        const resolution: ResolutionValueAny =
          subquestion == null ?
            resolutionsParent[k]!
          : (resolutionsThis[k] ??
            ({type: 'override', position: resolutionsParent[k]?.position} as ResolutionValueOverride))
        const resolutionParent = subquestion == null ? undefined : resolutionsParent[k]
        return {
          id: k,
          resolution,
          resolutionParent,
          newOverride:
            subquestion != null && resolutionsThis[k] == null && resolution != null && isOverride(resolution),
          resolutionDisplay:
            isOverride(resolution) ?
              ({...resolutionParent, ...compactResolution(resolution)} as ResolutionCfg)
            : (resolution as ResolutionCfg)
        }
      }),
      r => r.resolution.position ?? Number.MAX_SAFE_INTEGER
    ).map((r, position) => ({...r, resolution: {...r.resolution, position} as ResolutionCfg}))
    return combined
  }

  export type ResolutionItem = ReturnType<typeof resolutions>[number]

  /** the upstream forecast type to key forecasts with */
  export const key = (p: ResolutionKeyDependents) =>
    match(p.subquestionType)
      .with('cause', 'related', () => p.subquestionForecastType)
      .otherwise(() => p.questionForecastType)
  export const isOverride = (r: ResolutionValueAny): r is ResolutionValueOverride => 'type' in r && r.type == 'override'
  export const isStandard = (r: ResolutionValueAny): r is ResolutionValueStandard | ResolutionCfg => !isOverride(r)

  const zValueKey = z.object({
    type: zQuestionForecastType,
    subquestion_id: zx.nullish.string,
    code: zx.nullish.string
  })
  type ValueKey = z.infer<typeof zValueKey>
  const groupKeySerializer = queryStringSerializer(zValueKey)
  /** serialize the full key to persist a resolution of a forecast */
  export const resolutionKeyString = (key: ValueKey): string =>
    groupKeySerializer.serialize(zValueKey.strip().parse(compactObj(key)))
  /** deserialize a resolution key string to a value key */
  export const resolutionKey = (key: string) => groupKeySerializer.deserialize(key) as ValueKey

  export const defaultQuestionResolutions = (forecastType: QuestionForecastType) =>
    match(forecastType)
      .with(
        'likelihood',
        () =>
          ({
            'question-true': {
              type: 'standard',
              label: 'If question is true',
              labelShort: 'true',
              enableForecasts: true,
              description: ''
            },
            'question-false': {
              type: 'standard',
              label: 'If question is false',
              labelShort: 'false',
              enableForecasts: false,
              description: ''
            }
          }) satisfies Resolutions['likelihood']
      )
      .with(
        'amount',
        () =>
          ({
            'question-gt': {
              type: 'standard',
              label: 'If question more than X',
              labelShort: 'more than X',
              enableForecasts: true,
              description: ''
            },
            'question-lt': {
              type: 'standard',
              label: 'If question less than X',
              labelShort: 'less than X',
              enableForecasts: false,
              description: ''
            }
          }) satisfies Resolutions['amount']
      )
      .exhaustive()

  export const defaultSubquestionResolutions = (d: ResolutionKeyDependents): Resolutions['amount' | 'likelihood'] => {
    const upstreamType = key(d)
    return match({subquestionType: d.subquestionType, upstreamType})
      .with(
        {subquestionType: 'cause', upstreamType: 'likelihood'},
        () =>
          ({
            'cause-true': {
              type: 'standard',
              label: 'Top level question if cause is true',
              labelShort: 'true',
              enableForecasts: true,
              description: ''
            },
            'cause-false': {
              type: 'standard',
              label: 'Top level question if cause is false',
              labelShort: 'false',
              enableForecasts: false,
              description: ''
            }
          }) satisfies Resolutions['likelihood']
      )
      .with(
        {subquestionType: 'cause', upstreamType: 'amount'},
        () =>
          ({
            'cause-gt': {
              type: 'standard',
              label: 'Top level question if cause more than X',
              labelShort: 'more than x',
              enableForecasts: true,
              description: ''
            },
            'cause-lt': {
              type: 'standard',
              label: 'Top level question if cause less than X',
              labelShort: 'less than X',
              enableForecasts: false,
              description: ''
            }
          }) satisfies Resolutions['amount']
      )
      .otherwise(() => ({}))
  }
}
